import type { Message } from '@nexo/types';
import { daysFromToday, formatDay } from '../../lib/format';

/** Messages from the same author within this window are shown without repeating the header. */
const GROUP_WINDOW_MS = 5 * 60 * 1000;

export type TimelineItem =
  | { type: 'day'; key: string; label: string }
  | { type: 'message'; key: string; message: Message; compact: boolean };

/** Adds day separators and groups consecutive messages by author. */
export function buildTimeline(messages: Message[], now = new Date()): TimelineItem[] {
  const items: TimelineItem[] = [];
  let previous: Message | undefined;
  for (const message of messages) {
    const date = new Date(message.createdAt);
    const newDay = !previous || daysFromToday(new Date(previous.createdAt), date) !== 0;
    if (newDay) {
      items.push({
        type: 'day',
        key: `day-${message.createdAt.slice(0, 10)}-${message.id}`,
        label: formatDay(message.createdAt, now),
      });
    }
    const compact =
      !newDay &&
      previous?.author.id === message.author.id &&
      // Webhooks can post under different names; each name is its own group.
      previous.author.displayName === message.author.displayName &&
      date.getTime() - new Date(previous.createdAt).getTime() < GROUP_WINDOW_MS;
    items.push({ type: 'message', key: message.clientId ?? message.id, message, compact });
    previous = message;
  }
  return items;
}

/** Splits text into plain and URL segments so links can be rendered safely (no HTML injection). */
export function linkify(text: string): { text: string; href?: string }[] {
  const parts: { text: string; href?: string }[] = [];
  const pattern = /\bhttps?:\/\/[^\s<>"']+[^\s<>"'.,;:!?)\]]/g;
  let last = 0;
  for (const match of text.matchAll(pattern)) {
    if (match.index > last) parts.push({ text: text.slice(last, match.index) });
    parts.push({ text: match[0], href: match[0] });
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
}
